/**
 * The RFP's "Assigned Analyst" slot is filled by either a Centro analyst (keyed by
 * id) or a broker_analyst (Auth0-sourced, keyed by email — broker_analysts have no
 * Centro analyst id). The two populations share one dropdown but map to different,
 * mutually exclusive request fields. See ADR 10.19 amendment.
 */

import type { RfpDetailsResponse, UpdateRfpDetailsRequest } from "./api/types";

export type AssignedAnalyst =
  | { kind: "none" }
  | { kind: "centroAnalyst"; id: number; name: string }
  | { kind: "brokerAnalyst"; email: string; name: string };

export const NO_ASSIGNED_ANALYST: AssignedAnalyst = { kind: "none" };

export interface AnalystOption {
  /** Unique across both populations, so the dropdown can round-trip a selection */
  value: string;
  label: string;
  assignment: AssignedAnalyst;
}

export const assignedAnalystName = (
  assigned: AssignedAnalyst,
): string | undefined => (assigned.kind === "none" ? undefined : assigned.name);

export const analystOptionValue = (assigned: AssignedAnalyst): string => {
  switch (assigned.kind) {
    case "centroAnalyst":
      return `centroAnalyst:${assigned.id}`;
    case "brokerAnalyst":
      return `brokerAnalyst:${assigned.email}`;
    case "none":
      return "";
  }
};

const toAnalystOption = (assignment: AssignedAnalyst): AnalystOption => ({
  value: analystOptionValue(assignment),
  label: assignedAnalystName(assignment) ?? "",
  assignment,
});

/**
 * Reads the persisted Assigned Analyst off the RFP details response
 */
export const toAssignedAnalyst = (
  details: RfpDetailsResponse,
): AssignedAnalyst => {
  // A broker_analyst removed from Auth0 comes back with only an email, so fall
  // back to the email for the name rather than rendering a blank.
  if (details.assignedBrokerAnalyst) {
    return {
      kind: "brokerAnalyst",
      email: details.assignedBrokerAnalyst.email,
      name:
        details.assignedBrokerAnalyst.name ||
        details.assignedBrokerAnalyst.email,
    };
  }
  if (details.primaryAnalyst) {
    return {
      kind: "centroAnalyst",
      id: details.primaryAnalyst.id,
      name: details.primaryAnalyst.name,
    };
  }
  return NO_ASSIGNED_ANALYST;
};

/**
 * Builds the dropdown options from both analyst populations
 */
export const buildAnalystOptions = (
  details: RfpDetailsResponse | undefined,
): AnalystOption[] => {
  if (!details) return [];

  const options = [
    ...details.analystsList.map((analyst) =>
      toAnalystOption({
        kind: "centroAnalyst",
        id: analyst.id,
        name: analyst.name,
      }),
    ),
    ...details.brokerAnalystsList.map((brokerAnalyst) =>
      toAnalystOption({
        kind: "brokerAnalyst",
        email: brokerAnalyst.email,
        name: brokerAnalyst.name || brokerAnalyst.email,
      }),
    ),
  ];

  // A broker_analyst removed from Auth0 is still assigned but no longer listed;
  // keep the assignment selectable so opening the dropdown does not drop it.
  const assigned = toAssignedAnalyst(details);
  const assignedOption = toAnalystOption(assigned);
  const isAssignedListed = options.some(
    (option) => option.value === assignedOption.value,
  );
  if (assigned.kind === "none" || isAssignedListed) return options;

  return [...options, assignedOption];
};

/**
 * Maps the selection onto the update request. The two fields are mutually
 * exclusive — sending both is rejected by the backend.
 */
export const buildAssignedAnalystPayload = (
  assigned: AssignedAnalyst,
): Pick<
  UpdateRfpDetailsRequest,
  "primaryAnalystId" | "assignedBrokerAnalystEmail"
> => {
  switch (assigned.kind) {
    case "centroAnalyst":
      return { primaryAnalystId: assigned.id };
    case "brokerAnalyst":
      return { assignedBrokerAnalystEmail: assigned.email };
    case "none":
      return {};
  }
};
